import { createClient } from "@supabase/supabase-js";
import type { Database } from "../types/supabase";
import { getCachedData, setCachedData } from "./cache";

// Request deduplication map to prevent duplicate API calls
const pendingRequests = new Map<string, Promise<any>>();

// Get environment variables with fallbacks
const supabaseUrl =
  typeof import.meta !== "undefined" && import.meta.env
    ? import.meta.env.VITE_SUPABASE_URL
    : process.env.VITE_SUPABASE_URL || process.env.SUPABASE_URL;
const supabaseKey =
  typeof import.meta !== "undefined" && import.meta.env
    ? import.meta.env.VITE_SUPABASE_ANON_KEY
    : process.env.VITE_SUPABASE_ANON_KEY || process.env.SUPABASE_ANON_KEY;

// Log the values for debugging
console.log("Supabase credentials check:", {
  url: supabaseUrl ? "Present" : "Missing",
  key: supabaseKey ? "Present" : "Missing",
});

if (!supabaseUrl || !supabaseKey) {
  console.error("Missing Supabase credentials:", {
    url: supabaseUrl ? "Present" : "Missing (check .env file)",
    key: supabaseKey ? "Present" : "Missing (check .env file)",
  });
  if (typeof window !== "undefined") {
    // Only throw in browser context
    throw new Error(
      "Missing Supabase credentials. Please check your .env file.",
    );
  }
}

// Create Supabase client with enhanced configuration
export const supabase = createClient<Database>(supabaseUrl, supabaseKey, {
  auth: {
    autoRefreshToken: true,
    persistSession: true,
    detectSessionInUrl: true,
  },
  global: {
    headers: {
      "x-application-name": "one80labs-site",
    },
  },
  db: {
    schema: "public",
  },
  // Add custom fetch options
  fetchOptions: {
    // Increase timeout
    timeout: 60000, // Increased to 60 seconds
    credentials: "include", // Add credentials for CORS
  },
});

// Enhanced query function with retry logic, timeout, and request deduplication
export const enhancedQuery = async (
  queryFn: (signal: AbortSignal) => Promise<any>,
  options: {
    timeout?: number;
    retries?: number;
    retryDelay?: number;
    cacheKey?: string; // Add cache key for deduplication
  } = {},
) => {
  const {
    timeout = 60000, // Increased to 60 seconds
    retries = 5, // Increased retry attempts
    retryDelay = 2000, // Increased delay between retries
    cacheKey,
  } = options;

  // Check for pending request with same cache key
  if (cacheKey && pendingRequests.has(cacheKey)) {
    console.log(`🔄 Reusing pending request for: ${cacheKey}`);
    return pendingRequests.get(cacheKey);
  }

  // Create the query promise
  const queryPromise = (async () => {
    let attempt = 0;

    while (attempt < retries) {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort("timeout"), timeout);

      try {
        const result = await queryFn(controller.signal);
        clearTimeout(timeoutId);
        return result;
      } catch (error: any) {
        clearTimeout(timeoutId);

        // Don't retry if aborted or component unmounted
        if (
          error.message?.includes("unmount") ||
          error.message?.includes("aborted")
        ) {
          throw error;
        }

        attempt++;
        console.warn(`Query attempt ${attempt} failed:`, error);

        if (attempt === retries) {
          if (error.message?.includes("fetch")) {
            throw new Error(
              "Unable to connect to Supabase. Please check your network connection and try again.",
            );
          }
          throw error;
        }

        // Wait before retrying with exponential backoff
        await new Promise((resolve) =>
          setTimeout(resolve, retryDelay * Math.pow(2, attempt - 1)),
        );
      }
    }
  })();

  // Store the promise in pending requests if cache key provided
  if (cacheKey) {
    pendingRequests.set(cacheKey, queryPromise);

    // Clean up the pending request after completion
    queryPromise.finally(() => {
      pendingRequests.delete(cacheKey);
    });
  }

  return queryPromise;
};

// Test Supabase connection with increased timeout and retries
export const testSupabaseConnection = async () => {
  try {
    const { data, error } = await enhancedQuery(
      async (signal) =>
        supabase
          .from("surveys")
          .select("id", { count: "exact" })
          .limit(1)
          .abortSignal(signal),
      {
        timeout: 30000, // 30 second timeout for connection test
        retries: 5, // More retries for initial connection test
        retryDelay: 2000, // Longer delay between retries
      },
    );

    if (error) {
      console.error("Supabase connection error:", error);
      return false;
    }

    console.log("Supabase connection successful");
    return true;
  } catch (error) {
    console.error("Connection test failed:", error);
    return false;
  }
};

// Get first active survey with enhanced error handling
const getFirstActiveSurvey = async (): Promise<string | null> => {
  try {
    const { data, error } = await enhancedQuery(async (signal) =>
      supabase
        .from("surveys")
        .select("id")
        .eq("is_active", true)
        .order("created_at", { ascending: false })
        .limit(1)
        .single()
        .abortSignal(signal),
    );

    if (error) {
      console.error("Error fetching first active survey:", error);
      return null;
    }

    return data?.id || null;
  } catch (error) {
    console.error("Error in getFirstActiveSurvey:", error);
    return null;
  }
};

// Get blog post by slug with enhanced error handling and request deduplication
export const getBlogPost = async (slug: string, isPreview: boolean = false) => {
  try {
    const cacheKey = `blog_post_${slug}${isPreview ? "_preview" : ""}`;
    const cachedData = getCachedData(cacheKey);

    if (cachedData && !isPreview) {
      return cachedData;
    }

    const query = supabase
      .from("blog_posts")
      .select(
        `
        *,
        blog_authors (*),
        blog_categories (*),
        blog_tag_posts (
          blog_tags (*)
        )
      `,
      )
      .eq("slug", slug)
      .single();

    // Only apply published filter if not in preview mode
    if (!isPreview) {
      query.eq("is_published", true);
    }

    const { data, error } = await enhancedQuery(
      async (signal) => query.abortSignal(signal),
      { cacheKey: `blog_post_query_${slug}${isPreview ? "_preview" : ""}` },
    );

    if (error) {
      if (error.code === "PGRST116") {
        throw new Error("Blog post not found");
      }
      throw error;
    }

    if (!data) {
      throw new Error("Blog post not found");
    }

    if (!isPreview && !data.is_published) {
      throw new Error("This blog post is not published yet");
    }

    // Cache the result if not in preview mode
    if (!isPreview) {
      setCachedData(cacheKey, data, 300); // Cache for 5 minutes
    }

    return data;
  } catch (error) {
    console.error("Error fetching blog post:", error);
    throw error;
  }
};

// Get blog categories with enhanced error handling
export const getBlogCategories = async () => {
  try {
    const cacheKey = "blog_categories";
    const cachedData = getCachedData(cacheKey);

    if (cachedData) {
      return cachedData;
    }

    const { data, error } = await enhancedQuery(
      async (signal) =>
        supabase
          .from("blog_categories")
          .select("*")
          .order("name", { ascending: true })
          .abortSignal(signal),
      { cacheKey: "blog_categories_query" },
    );

    if (error) {
      console.error("Error fetching blog categories:", error);
      throw error;
    }

    // Cache the categories for 5 minutes
    setCachedData(cacheKey, data, 300);

    return data;
  } catch (error) {
    console.error("Error in getBlogCategories:", error);
    throw error;
  }
};

// Get blog posts by category with enhanced error handling
export const getBlogPostsByCategory = async (
  categorySlug: string,
  page: number = 1,
) => {
  try {
    const limit = 10;
    const offset = (page - 1) * limit;

    const cacheKey = `blog_posts_category_${categorySlug}_page_${page}`;
    const cachedData = getCachedData(cacheKey);

    if (cachedData) {
      return cachedData;
    }

    // First get the category ID
    const { data: category, error: categoryError } = await enhancedQuery(
      async (signal) =>
        supabase
          .from("blog_categories")
          .select("id")
          .eq("slug", categorySlug)
          .single()
          .abortSignal(signal),
    );

    if (categoryError) {
      throw new Error("Category not found");
    }

    // Then get the posts for this category
    const {
      data: posts,
      error: postsError,
      count,
    } = await enhancedQuery(async (signal) =>
      supabase
        .from("blog_posts")
        .select(
          `
          *,
          blog_authors (*),
          blog_categories (*)
        `,
          { count: "exact" },
        )
        .eq("category_id", category.id)
        .eq("is_published", true)
        .eq("deleted", false)
        .order("published_at", { ascending: false })
        .range(offset, offset + limit - 1)
        .abortSignal(signal),
    );

    if (postsError) {
      throw postsError;
    }

    const result = {
      posts: posts || [],
      total: count || 0,
    };

    // Cache the results for 5 minutes
    setCachedData(cacheKey, result, 300);

    return result;
  } catch (error) {
    console.error("Error fetching posts by category:", error);
    throw error;
  }
};

// Get blog authors with enhanced error handling
export const getBlogAuthors = async () => {
  try {
    const cacheKey = "blog_authors";
    const cachedData = getCachedData(cacheKey);

    if (cachedData) {
      return cachedData;
    }

    const { data, error } = await enhancedQuery(async (signal) =>
      supabase
        .from("blog_authors")
        .select("*")
        .order("name", { ascending: true })
        .abortSignal(signal),
    );

    if (error) {
      console.error("Error fetching blog authors:", error);
      throw error;
    }

    // Cache the authors for 5 minutes
    setCachedData(cacheKey, data, 300);

    return data;
  } catch (error) {
    console.error("Error in getBlogAuthors:", error);
    throw error;
  }
};

// Get blog posts by author with enhanced error handling
export const getBlogPostsByAuthor = async (
  authorSlug: string,
  page: number = 1,
) => {
  try {
    const limit = 10;
    const offset = (page - 1) * limit;

    const cacheKey = `blog_posts_author_${authorSlug}_page_${page}`;
    const cachedData = getCachedData(cacheKey);

    if (cachedData) {
      return cachedData;
    }

    // First get the author ID
    const { data: author, error: authorError } = await enhancedQuery(
      async (signal) =>
        supabase
          .from("blog_authors")
          .select("id")
          .eq("slug", authorSlug)
          .single()
          .abortSignal(signal),
    );

    if (authorError) {
      throw new Error("Author not found");
    }

    // Then get the posts for this author
    const {
      data: posts,
      error: postsError,
      count,
    } = await enhancedQuery(async (signal) =>
      supabase
        .from("blog_posts")
        .select(
          `
          *,
          blog_authors (*),
          blog_categories (*)
        `,
          { count: "exact" },
        )
        .eq("author_id", author.id)
        .eq("is_published", true)
        .eq("deleted", false)
        .order("published_at", { ascending: false })
        .range(offset, offset + limit - 1)
        .abortSignal(signal),
    );

    if (postsError) {
      throw postsError;
    }

    const result = {
      posts: posts || [],
      total: count || 0,
    };

    // Cache the results for 5 minutes
    setCachedData(cacheKey, result, 300);

    return result;
  } catch (error) {
    console.error("Error fetching posts by author:", error);
    throw error;
  }
};

// Initialize connection test
testSupabaseConnection()
  .then((isConnected) => {
    console.log("Supabase connection:", isConnected ? "OK" : "Failed");
  })
  .catch(console.error);
